import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/authContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  DollarSign,
  Zap,
  MessageSquare,
  Car,
  Shield,
  X,
  FileText,
  Globe,
  MapPin,
  Building,
  UserPlus,
  Settings,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronFirst,
  Gift,
  Handshake,
  Clock,
  AlertCircle,
  Route,
  Mail,
  Plug,
  Radio,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Each nav item has a `permission` field matching the permission name from the roles system.
// All items require explicit permission — items without `permission` are always visible.
const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard, permission: 'View Dashboard' },
  {
    name: 'User Management',
    href: '/admin/users',
    icon: UserPlus,
    permission: 'View Users',
    children: [
      { name: 'Users', href: '/admin/users/list', icon: Users, permission: 'View Users' },
      { name: 'Roles', href: '/admin/users/roles', icon: Shield, permission: 'View Roles' },
      { name: 'Permissions', href: '/admin/users/permissions', icon: Settings, permission: 'View Permissions' }
    ]
  },
  {
    name: 'Config Management',
    href: '/admin/config',
    icon: Globe,
    permission: 'View Countries',
    children: [
      { name: 'Countries', href: '/admin/config/countries', icon: Globe, permission: 'View Countries' },
      { name: 'States', href: '/admin/config/states', icon: MapPin, permission: 'View States' },
      { name: 'Cities', href: '/admin/config/cities', icon: Building, permission: 'View Cities' }
    ]
  },
  { name: 'Drivers', href: '/admin/drivers', icon: Car, permission: 'View Drivers' },
  { name: 'Riders', href: '/admin/riders', icon: Users, permission: 'View Riders' },
  {
    name: 'Pricing',
    href: '/admin/vehicle-categories',
    icon: DollarSign,
    permission: 'View Vehicle Categories',
    children: [
      { name: 'Vehicle Categories', href: '/admin/vehicle-categories', icon: Car, permission: 'View Vehicle Categories' },
      { name: 'Surge Pricing', href: '/admin/surge-pricing', icon: Zap, permission: 'View Surge Pricing' }
    ]
  },
  { name: 'Promotions', href: '/admin/promotions', icon: Gift, permission: 'View Promotions' },
  // { name: 'Referral Campaigns', href: '/admin/referral-campaigns', icon: Handshake, permission: 'View Referral Campaigns' },
  { name: 'Disputes', href: '/admin/disputes', icon: MessageSquare, permission: 'View Disputes' },
  { name: 'Reports', href: '/admin/reports', icon: FileText, permission: 'View Reports' },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: AlertCircle, permission: 'View Audit Logs' },
  {
    name: 'Settings',
    href: '/admin/settings',
    icon: Settings,
    permission: 'View Settings',
    children: [
      { name: 'Distance & Wait Time', href: '/admin/distance-wait-time-settings', icon: Clock, permission: 'View Distance & Wait Settings' },
      { name: 'Email Settings', href: '/admin/settings/email', icon: Mail, permission: 'View Email Settings' },
      { name: 'SMS Settings', href: '/admin/settings/sms', icon: MessageSquare, permission: 'View SMS Settings' }
    ]
  },
  {
    name: 'Security',
    href: '/admin/security',
    icon: Shield,
    children: [
      { name: 'Change Password', href: '/admin/security/change-password', icon: Shield }
    ]
  }
];

const Sidebar = ({ sidebarOpen, setSidebarOpen, isCollapsed, setIsCollapsed }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  // Check if user has a specific permission by name
  const userPermissions: string[] = React.useMemo(() => {
    if (!user) return [];
    return (user.permissions ?? []).map((p: any) => typeof p === 'string' ? p : p.name || '').filter(Boolean);
  }, [user]);

  const isSuperAdmin = React.useMemo(() => {
    if (!user) return false;
    const role = (user.role || '').replace(/^ROLE_/, '').toLowerCase();
    const roles = (user.roles ?? []).map((r: any) => String(r).replace(/^ROLE_/, '').toLowerCase());
    return role === 'super_admin' || roles.includes('super_admin');
  }, [user]);

  const hasNavPermission = (permissionName?: string) => {
    if (!permissionName) return true; // No permission required = always visible
    if (isSuperAdmin) return true; // Super admins see everything
    return userPermissions.includes(permissionName);
  };

  // Filter navigation based on permissions
  const filteredNavigation = React.useMemo(() => {
    return navigation
      .map(item => {
        if (!item.children) {
          return hasNavPermission(item.permission) ? item : null;
        }
        // For parent items with children, show parent if it has permission OR any child has permission
        const filteredChildren = item.children.filter(child => hasNavPermission(child.permission));
        if (hasNavPermission(item.permission) || filteredChildren.length > 0) {
          return { ...item, children: filteredChildren.length > 0 ? filteredChildren : item.children };
        }
        return null;
      })
      .filter(Boolean);
  }, [userPermissions, isSuperAdmin]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(item => item !== itemName)
        : [...prev, itemName]
    );
  };

  // Helper to get pathname from href (removes query params)
  const getPathname = (href: string) => {
    try {
      return new URL(href, window.location.origin).pathname;
    } catch {
      return href.split('?')[0];
    }
  };

  // Check if an item or its children are active
  const isItemActive = (item) => {
    const itemPathname = getPathname(item.href);
    if (location.pathname === itemPathname) return true;
    if (item.children) {
      return item.children.some(child => location.pathname === getPathname(child.href));
    }
    return false;
  };

  // Auto-expand sections if any of their children are active
  React.useEffect(() => {
    const sectionsWithChildren = filteredNavigation.filter(item => item.children);

    sectionsWithChildren.forEach(section => {
      if (section.children?.some(child => location.pathname === getPathname(child.href))) {
        setExpandedItems(prev => [...prev.filter(item => item !== section.name), section.name]);
      }
    });
  }, [location.pathname]);

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-sidebar/95 backdrop-blur-sm border-r border-sidebar-border transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:bg-sidebar lg:backdrop-blur-none",
          isCollapsed ? "w-16" : "w-64",
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={cn(
            "flex items-center justify-between border-b border-sidebar-border",
            isCollapsed ? "px-4 py-6" : "p-3"
          )}>
            <div className="flex items-center space-x-1">
              <img
                src="/assets/logo/Aboosto-03.svg"
                alt="Aboosto Logo"
                className={cn(
                  "transition-all duration-300",
                  isCollapsed ? "w-8 h-8" : "w-24 h-24"
                )}
                onError={(e) => {
                  // Fallback to PNG if SVG fails
                  const target = e.target as HTMLImageElement;
                  target.src = '/assets/logo/Aboosto-03.png';
                }}
              />
              {!isCollapsed && (
                <div>
                  <p className="text-xs text-muted-foreground">Admin Portal</p>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                className="hidden lg:flex"
                onClick={() => setIsCollapsed(!isCollapsed)}
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <span className="text-sm font-bold">&gt;</span>
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className={cn(
            "flex-1 py-6 space-y-2 overflow-y-auto",
            isCollapsed ? "px-2" : "px-4"
          )}>
            {filteredNavigation.map((item) => {
              const isActive = isItemActive(item);
              const isExpanded = expandedItems.includes(item.name);

              if (item.children) {
                if (isCollapsed) {
                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => {
                          toggleExpanded(item.name);
                          setIsCollapsed(false);
                        }}
                        className={cn(
                          "w-full group flex items-center justify-center px-2 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative",
                          isActive
                            ? 'bg-primary text-primary-foreground shadow-glow'
                            : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
                        )}
                        title={item.name}
                      >
                        <item.icon
                          className={cn(
                            "h-5 w-5 transition-colors",
                            isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                          )}
                        />
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={cn(
                        "w-full group flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200",
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-glow'
                          : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
                      )}
                    >
                      <div className="flex items-center">
                        <item.icon
                          className={cn(
                            "mr-3 h-5 w-5 transition-colors",
                            isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                          )}
                        />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="mt-1 ml-8 space-y-1">
                        {item.children.map((child) => {
                          const isChildActive = location.pathname === getPathname(child.href);
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={cn(
                                "group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                                isChildActive
                                  ? 'bg-primary text-primary-foreground shadow-glow'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <child.icon
                                className={cn(
                                  "mr-3 h-4 w-4 transition-colors",
                                  isChildActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                                )}
                              />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              if (isCollapsed) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "group flex items-center justify-center px-2 py-3 text-sm font-medium rounded-lg transition-all duration-200",
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-glow'
                        : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={item.name}
                  >
                    <item.icon
                      className={cn(
                        "h-5 w-5 transition-colors",
                        isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                      )}
                    />
                  </Link>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200",
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon
                    className={cn(
                      "mr-3 h-5 w-5 transition-colors",
                      isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={cn(
            "border-t border-sidebar-border",
            isCollapsed ? "px-2 py-4" : "p-4"
          )}>
            {isCollapsed ? (
              <div className="flex items-center justify-center">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center" title={user?.firstName || user?.name || 'Admin User'}>
                  <UserCheck className="w-4 h-4 text-primary-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3 p-3 rounded-lg bg-sidebar-accent">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{user?.firstName || user?.name || 'Admin User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || 'admin@aboosto.com'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;